import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Check, ArrowLeft, ArrowRight, Upload, X, AlertCircle, Plus, Trash2, Package } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STEPS = ["Categoría", "Producto", "Cantidad", "Técnica", "Detalles", "Diseño", "Entrega", "Resumen"];

const PRINT_AREAS = [
  { id: "frontal", label: "Frontal" },
  { id: "espalda", label: "Espalda" },
  { id: "frontal_espalda", label: "Frontal y Espalda" },
  { id: "manga_izq", label: "Manga Izquierda" },
  { id: "manga_der", label: "Manga Derecha" },
  { id: "completa", label: "Área Completa" },
];

const EMPTY_ITEM = {
  category: "", product_id: "", product_name: "",
  technique_id: "", technique_name: "",
  quantity: 50, colors: 1, print_area: "frontal", notes: "",
  is_custom: false, custom_description: "",
};

export default function QuoteBuilder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [products, setProducts] = useState([]);
  const [techniques, setTechniques] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [quotePreview, setQuotePreview] = useState(null);
  const [currentItemIdx, setCurrentItemIdx] = useState(0);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [nit, setNit] = useState("CF");
  const [globalNotes, setGlobalNotes] = useState("");
  const [designFiles, setDesignFiles] = useState([]);
  const [uploadedFileIds, setUploadedFileIds] = useState([]);
  const [discountSettings, setDiscountSettings] = useState({ discount_enabled: true, discount_min_qty: 300, discount_percent: 5 });

  const item = items[currentItemIdx] || EMPTY_ITEM;

  const updateItem = (field, value) => {
    setItems((prev) => prev.map((it, i) => i === currentItemIdx ? { ...it, [field]: value } : it));
  };

  useEffect(() => {
    API.get("/products").then((r) => setProducts(r.data)).catch((err) => { if (process.env.NODE_ENV === "development") console.error("Products fetch failed:", err.message); });
    API.get("/techniques").then((r) => setTechniques(r.data)).catch((err) => { if (process.env.NODE_ENV === "development") console.error("Techniques fetch failed:", err.message); });
    API.get("/settings").then((r) => setDiscountSettings({ discount_enabled: r.data.discount_enabled ?? true, discount_min_qty: r.data.discount_min_qty ?? 300, discount_percent: r.data.discount_percent ?? 5 })).catch(() => {});
  }, []);

  const filteredProducts = useMemo(() => products.filter((p) => p.category === item.category), [products, item.category]);

  // Filter techniques based on selected product's applicable_techniques
  const selectedProduct = useMemo(() => products.find((p) => p.id === item.product_id), [products, item.product_id]);
  const filteredTechniques = useMemo(() => {
    if (selectedProduct?.applicable_techniques?.length > 0) {
      return techniques.filter((t) => selectedProduct.applicable_techniques.includes(t.id));
    }
    return techniques;
  }, [selectedProduct, techniques]);

  const fetchQuotePreview = useCallback(async () => {
    const validItems = items.filter((it) => it.product_id && it.technique_id && it.quantity > 0);
    if (validItems.length === 0) return;
    try {
      const { data } = await API.post("/quick-quote", validItems.map((it) => ({
        category: it.category, product_id: it.product_id, product_name: it.product_name,
        technique_id: it.technique_id, technique_name: it.technique_name,
        quantity: it.quantity, colors: it.colors, print_area: it.print_area,
        notes: it.notes, is_custom: it.is_custom, custom_description: it.custom_description,
      })));
      setQuotePreview(data);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Quote preview failed:", err.message);
    }
  }, [items]);

  useEffect(() => { if (step >= 3) fetchQuotePreview(); }, [fetchQuotePreview, step]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setDesignFiles((prev) => [...prev, { id: data.id, name: file.name }]);
        setUploadedFileIds((prev) => [...prev, data.id]);
      } catch (err) {
        setError("Error al subir archivo: " + (err.response?.data?.detail || err.message));
      }
    }
  };

  const removeFile = (id) => {
    setDesignFiles((prev) => prev.filter((f) => f.id !== id));
    setUploadedFileIds((prev) => prev.filter((fid) => fid !== id));
  };

  const addItem = () => {
    setItems((prev) => [...prev, { ...EMPTY_ITEM }]);
    setCurrentItemIdx(items.length);
    setStep(0);
  };

  const removeItem = (idx) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, i) => i !== idx));
    if (currentItemIdx >= idx && currentItemIdx > 0) setCurrentItemIdx(currentItemIdx - 1);
  };

  const switchToItem = (idx) => {
    setCurrentItemIdx(idx);
    setStep(0);
  };

  const handleSubmit = async () => {
    if (!user) { navigate("/login"); return; }
    setSubmitting(true);
    setError("");
    try {
      await API.post("/quotations", {
        items: items.filter((it) => it.product_id && it.technique_id).map((it) => ({
          category: it.category, product_id: it.product_id, product_name: it.product_name,
          technique_id: it.technique_id, technique_name: it.technique_name,
          quantity: it.quantity, colors: it.colors, print_area: it.print_area,
          notes: it.notes, is_custom: it.is_custom, custom_description: it.custom_description,
        })),
        delivery_date_requested: deliveryDate || null,
        delivery_address: deliveryAddress,
        nit: nit || "CF",
        notes: globalNotes,
        design_file_ids: uploadedFileIds,
      });
      navigate("/portal/cotizaciones");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const canNext = () => {
    if (step === 0) return !!item.category;
    if (step === 1) return !!item.product_id;
    if (step === 2) return item.quantity > 0;
    if (step === 3) return !!item.technique_id;
    return true;
  };

  const SelectCard = ({ selected, onClick, title, subtitle, testId }) => (
    <button onClick={onClick} data-testid={testId}
      className={`text-left p-4 border rounded-sm transition-colors w-full ${selected ? "border-[#0047AB] bg-[#F0F4FF]" : "border-[#E2E8F0] bg-white hover:border-[#0047AB]"}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-[#0F172A]">{title}</p>
          {subtitle && <p className="text-xs text-[#475569] mt-1">{subtitle}</p>}
        </div>
        {selected && <Check className="w-5 h-5 text-[#0047AB]" />}
      </div>
    </button>
  );

  const renderStep = () => {
    switch (step) {
      case 0: return (
        <div className="space-y-3" data-testid="step-category">
          <h3 className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Selecciona la categoría</h3>
          <SelectCard selected={item.category === "textil"} onClick={() => { updateItem("category", "textil"); updateItem("product_id", ""); updateItem("product_name", ""); updateItem("is_custom", false); }} title="Textil" subtitle="Playeras, polos, sudaderas, uniformes" testId="cat-textil" />
          <SelectCard selected={item.category === "promocionales"} onClick={() => { updateItem("category", "promocionales"); updateItem("product_id", ""); updateItem("product_name", ""); updateItem("is_custom", false); }} title="Promocionales" subtitle="Gorras, tazas, bolsas, llaveros y más" testId="cat-promocionales" />
        </div>
      );
      case 1: return (
        <div className="space-y-3" data-testid="step-product">
          <h3 className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Selecciona el producto</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredProducts.map((p) => {
              const imgUrl = p.image_file_id ? `${API_BASE}/api/public/files/${p.image_file_id}` : null;
              const selected = item.product_id === p.id && !item.is_custom;
              return (
                <button key={p.id} onClick={() => { updateItem("product_id", p.id); updateItem("product_name", p.name); updateItem("is_custom", false); updateItem("custom_description", ""); }}
                  className={`text-left border rounded-sm transition-colors overflow-hidden ${selected ? "border-[#0047AB] bg-[#F0F4FF]" : "border-[#E2E8F0] bg-white hover:border-[#0047AB]"}`}
                  data-testid={`product-${p.id}`}>
                  <div className="aspect-[3/2] bg-[#F1F5F9] flex items-center justify-center overflow-hidden">
                    {imgUrl ? <img src={imgUrl} alt={p.name} className="w-full h-full object-cover" /> : <Package className="w-8 h-8 text-[#CBD5E1]" />}
                  </div>
                  <div className="p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">{p.name}</p>
                      <p className="text-xs text-[#475569] mt-0.5">Desde Q{p.base_price}</p>
                    </div>
                    {selected && <Check className="w-5 h-5 text-[#0047AB] shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>
          {/* Custom product option */}
          <div className="border-t border-[#E2E8F0] pt-3 mt-3">
            <SelectCard
              selected={item.is_custom}
              onClick={() => { updateItem("is_custom", true); updateItem("product_id", "custom"); updateItem("product_name", "Producto Personalizado"); }}
              title="Producto Personalizado"
              subtitle="Precio a cotizar - Describe el artículo que necesitas"
              testId="product-custom"
            />
            {item.is_custom && (
              <div className="mt-3">
                <Label className="text-sm text-[#475569]">Describe tu producto</Label>
                <Textarea
                  value={item.custom_description}
                  onChange={(e) => updateItem("custom_description", e.target.value)}
                  placeholder="Ej: Botella de vidrio 750ml con tapa de bambú, grabado láser con logo..."
                  className="mt-1 border-[#CBD5E1] rounded-sm"
                  rows={3}
                  data-testid="custom-product-description"
                />
                <div className="mt-2">
                  <Label className="text-sm text-[#475569]">Nombre corto del producto</Label>
                  <Input
                    value={item.product_name === "Producto Personalizado" ? "" : item.product_name}
                    onChange={(e) => updateItem("product_name", e.target.value || "Producto Personalizado")}
                    placeholder="Ej: Botella de Vidrio"
                    className="mt-1 border-[#CBD5E1] rounded-sm"
                    data-testid="custom-product-name"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      );
      case 2: return (
        <div className="space-y-4" data-testid="step-quantity">
          <h3 className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Cantidad</h3>
          <div>
            <Label className="text-sm text-[#475569]">Cantidad de piezas</Label>
            <Input type="number" min="1" value={item.quantity} onChange={(e) => updateItem("quantity", parseInt(e.target.value) || 0)} className="mt-1 border-[#CBD5E1] rounded-sm w-32" data-testid="quantity-input" />
          </div>
          <div className="bg-[#F0F4FF] border border-[#0047AB]/20 rounded-sm p-4">
            <p className="text-xs font-bold text-[#0047AB] uppercase tracking-wider mb-2">Descuentos por Volumen</p>
            {discountSettings.discount_enabled ? (
              <p className="text-xs text-[#475569]">{discountSettings.discount_min_qty}+ unidades: {discountSettings.discount_percent}% de descuento</p>
            ) : (
              <p className="text-xs text-[#94A3B8]">No hay descuentos por volumen activos</p>
            )}
          </div>
        </div>
      );
      case 3: return (
        <div className="space-y-3" data-testid="step-technique">
          <h3 className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Técnica de impresión</h3>
          {filteredTechniques.map((t) => (
            <SelectCard key={t.id} selected={item.technique_id === t.id} onClick={() => { updateItem("technique_id", t.id); updateItem("technique_name", t.name); }} title={t.name} subtitle={t.description} testId={`tech-${t.id}`} />
          ))}
        </div>
      );
      case 4: return (
        <div className="space-y-4" data-testid="step-details">
          <h3 className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Detalles de impresión</h3>
          <div>
            <Label className="text-sm text-[#475569]">Número de colores</Label>
            <Input type="number" min="1" max="8" value={item.colors} onChange={(e) => updateItem("colors", parseInt(e.target.value) || 1)} className="mt-1 border-[#CBD5E1] rounded-sm w-24" data-testid="colors-input" />
          </div>
          {!item.is_custom && (
            <div>
              <Label className="text-sm text-[#475569]">Área de impresión</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                {PRINT_AREAS.map((a) => (
                  <SelectCard key={a.id} selected={item.print_area === a.id} onClick={() => updateItem("print_area", a.id)} title={a.label} testId={`area-${a.id}`} />
                ))}
              </div>
            </div>
          )}
          <div>
            <Label className="text-sm text-[#475569]">Notas para este producto</Label>
            <Textarea value={item.notes} onChange={(e) => updateItem("notes", e.target.value)} placeholder="Detalles extra sobre este producto..." className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="notes-input" />
          </div>
        </div>
      );
      case 5: return (
        <div className="space-y-4" data-testid="step-design">
          <h3 className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Sube tu diseño</h3>
          <p className="text-sm text-[#475569]">Formatos aceptados: AI, PDF, PSD, PNG, JPG</p>
          <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#CBD5E1] rounded-sm p-8 cursor-pointer hover:border-[#0047AB] transition-colors" data-testid="file-upload-area">
            <Upload className="w-8 h-8 text-[#94A3B8] mb-2" />
            <span className="text-sm text-[#475569]">Clic para subir archivos</span>
            <input type="file" className="hidden" multiple accept=".ai,.pdf,.psd,.png,.jpg,.jpeg" onChange={handleFileUpload} />
          </label>
          {designFiles.length > 0 && (
            <div className="space-y-2">
              {designFiles.map((f) => (
                <div key={f.id} className="flex items-center justify-between bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm px-3 py-2">
                  <span className="text-sm text-[#0F172A] truncate">{f.name}</span>
                  <button onClick={() => removeFile(f.id)} className="text-[#94A3B8] hover:text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-[#94A3B8]">Este paso es opcional. Puedes enviar tu diseño después.</p>
        </div>
      );
      case 6: return (
        <div className="space-y-4" data-testid="step-delivery">
          <h3 className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Entrega y facturación</h3>
          <div>
            <Label className="text-sm text-[#475569]">Fecha deseada de entrega</Label>
            <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="mt-1 border-[#CBD5E1] rounded-sm w-48" data-testid="delivery-date-input" />
          </div>
          <div>
            <Label className="text-sm text-[#475569]">Dirección de entrega</Label>
            <Textarea value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} placeholder="Dirección completa para entrega o recoger en tienda..." className="mt-1 border-[#CBD5E1] rounded-sm" rows={2} data-testid="delivery-address-input" />
            <p className="text-xs text-[#94A3B8] mt-1">El costo de envío se calculará según la ubicación.</p>
          </div>
          <div>
            <Label className="text-sm text-[#475569]">NIT para facturación</Label>
            <div className="flex items-center gap-3 mt-1">
              <Input value={nit} onChange={(e) => setNit(e.target.value)} placeholder="NIT o CF" className="border-[#CBD5E1] rounded-sm w-48" data-testid="nit-input" />
              <button type="button" onClick={() => setNit("CF")} className={`px-3 py-2 text-xs font-medium rounded-sm border transition-colors ${nit === "CF" ? "bg-[#0047AB] text-white border-[#0047AB]" : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0047AB]"}`} data-testid="nit-cf-btn">
                Consumidor Final
              </button>
            </div>
          </div>
          <div>
            <Label className="text-sm text-[#475569]">Notas generales</Label>
            <Textarea value={globalNotes} onChange={(e) => setGlobalNotes(e.target.value)} placeholder="Notas generales para toda la cotización..." className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="global-notes-input" />
          </div>
        </div>
      );
      case 7: return (
        <div className="space-y-4" data-testid="step-summary">
          <h3 className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Resumen de Cotización</h3>
          
          {/* Items summary */}
          {items.filter((it) => it.product_id).map((it, i) => (
            <div key={`${it.product_id}-${it.technique_id}-${i}`} className="bg-white border border-[#E2E8F0] rounded-sm divide-y divide-[#E2E8F0]">
              <div className="px-4 py-2 bg-[#F8FAFC] flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-[#0047AB]">Producto {i + 1}{it.is_custom ? " (Personalizado)" : ""}</span>
              </div>
              <Row label="Producto" value={it.product_name} />
              <Row label="Cantidad" value={`${it.quantity} piezas`} />
              <Row label="Técnica" value={it.technique_name} />
              <Row label="Colores" value={it.colors} />
              {!it.is_custom && <Row label="Área" value={PRINT_AREAS.find((a) => a.id === it.print_area)?.label} />}
              {it.is_custom && it.custom_description && <Row label="Detalle" value={it.custom_description} />}
              {it.notes && <Row label="Notas" value={it.notes} />}
            </div>
          ))}

          {deliveryDate && <div className="bg-white border border-[#E2E8F0] rounded-sm"><Row label="Fecha deseada" value={deliveryDate} /></div>}
          {deliveryAddress && <div className="bg-white border border-[#E2E8F0] rounded-sm"><Row label="Dirección de entrega" value={deliveryAddress} /></div>}
          <div className="bg-white border border-[#E2E8F0] rounded-sm"><Row label="NIT" value={nit || "CF"} /></div>
          {designFiles.length > 0 && <div className="bg-white border border-[#E2E8F0] rounded-sm"><Row label="Archivos" value={`${designFiles.length} archivo(s)`} /></div>}

          {quotePreview && (
            <div className="bg-[#F0F4FF] border border-[#0047AB]/20 rounded-sm p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-[#0047AB] mb-3">Cotización Estimada</p>
              {quotePreview.items?.map((qi) => (
                <div key={`${qi.product_id}-${qi.technique_id}`} className="text-sm text-[#0F172A] mb-2 pb-2 border-b border-[#0047AB]/10 last:border-0">
                  <div className="flex justify-between font-medium">
                    <span>{qi.product_name}</span>
                    <span>{qi.is_custom ? "Precio a cotizar" : `Q${qi.subtotal?.toFixed(2)}`}</span>
                  </div>
                  {!qi.is_custom && (
                    <div className="text-xs text-[#475569] mt-1">
                      {qi.quantity} pzas x Q{qi.unit_price?.toFixed(2)}
                      {qi.discount_percent > 0 && <span className="text-green-600 ml-1">(-{qi.discount_percent}%)</span>}
                    </div>
                  )}
                </div>
              ))}
              <div className="flex justify-between mt-3 pt-3 border-t border-[#0047AB]/20 text-lg font-bold text-[#0F172A]">
                <span>Total:</span><span data-testid="quote-total">Q{quotePreview.total?.toFixed(2)}</span>
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-1">* Precios incluyen IVA e ISR</p>
            </div>
          )}

          {/* Estimated price notice */}
          <div className="bg-amber-50 border border-amber-300/40 rounded-sm p-4" data-testid="estimate-notice">
            <p className="text-sm font-medium text-[#92400E] mb-1">Este precio es un estimado</p>
            <p className="text-xs text-[#475569] leading-relaxed">Al enviar tu solicitud, nuestro equipo la revisará y te enviará la cotización final con el precio definitivo. Una vez aprobada por ti, se procederá con la producción.</p>
          </div>

          {!user && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">Necesitas iniciar sesión para enviar tu cotización. <a href="/login" className="text-[#0047AB] font-medium underline">Iniciar sesión</a></p>
            </div>
          )}
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="quote-builder-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-2">Cotizador</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Cotiza tu pedido</h1>
        </div>

        {/* Multi-item tabs */}
        {items.length > 0 && (
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
            {items.map((it, i) => (
              <button key={i} onClick={() => switchToItem(i)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors shrink-0 ${currentItemIdx === i ? "bg-[#0047AB] text-white border-[#0047AB]" : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0047AB]"}`}
                data-testid={`item-tab-${i}`}>
                {it.product_name || `Producto ${i + 1}`}
                {items.length > 1 && (
                  <span onClick={(e) => { e.stopPropagation(); removeItem(i); }} className="ml-1 hover:text-red-300">
                    <X className="w-3 h-3" />
                  </span>
                )}
              </button>
            ))}
            <Button variant="ghost" size="sm" onClick={addItem} className="rounded-sm text-[#0047AB] hover:bg-[#F0F4FF] shrink-0" data-testid="add-item-btn">
              <Plus className="w-4 h-4 mr-1" /> Agregar producto
            </Button>
          </div>
        )}

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between mb-2">
            {STEPS.map((s, i) => (
              <span key={i} className={`text-[10px] font-medium hidden sm:block ${i <= step ? "text-[#0047AB]" : "text-[#94A3B8]"}`}>{s}</span>
            ))}
          </div>
          <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5" />
          <p className="text-xs text-[#475569] mt-2 sm:hidden">Paso {step + 1} de {STEPS.length}: {STEPS[step]}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm flex items-start gap-2" data-testid="quote-error">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              {renderStep()}
              <div className="flex justify-between mt-8 pt-4 border-t border-[#E2E8F0]">
                <Button variant="ghost" onClick={() => setStep((s) => s - 1)} disabled={step === 0} className="text-[#475569] rounded-sm" data-testid="quote-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Atrás
                </Button>
                {step < STEPS.length - 1 ? (
                  <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} className="bg-[#0047AB] text-white font-bold rounded-sm hover:bg-[#003A8C] transition-colors" data-testid="quote-next-btn">
                    Siguiente <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={submitting || !user} className="bg-[#E30613] text-white font-bold rounded-sm hover:bg-[#C80410] transition-colors" data-testid="quote-submit-btn">
                    {submitting ? "Enviando..." : "Solicitar Cotización"}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="hidden lg:block">
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-4 sticky top-20">
              <h4 className="text-sm font-bold text-[#0F172A] mb-3" style={{ fontFamily: 'Chivo' }}>Tu Cotización</h4>
              <div className="space-y-3">
                {items.filter((it) => it.product_name).map((it, i) => (
                  <div key={`sidebar-${it.product_id}-${i}`} className="text-xs text-[#475569] pb-2 border-b border-[#E2E8F0] last:border-0">
                    <div className="font-medium text-[#0F172A]">{it.product_name}{it.is_custom ? " *" : ""}</div>
                    {it.quantity > 0 && <div>{it.quantity} pzas</div>}
                    {it.technique_name && <div>{it.technique_name}</div>}
                  </div>
                ))}
              </div>
              {quotePreview && (
                <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
                  <div className="flex justify-between text-sm font-bold text-[#0F172A]">
                    <span>Total:</span><span>Q{quotePreview.total?.toFixed(2)}</span>
                  </div>
                  <p className="text-[9px] text-[#94A3B8] mt-1">Incluye IVA e ISR</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between px-4 py-3">
      <span className="text-sm text-[#475569]">{label}</span>
      <span className="text-sm font-medium text-[#0F172A] text-right max-w-[60%]">{value}</span>
    </div>
  );
}
